import { useEffect, useRef, useState } from "react";
import { useNotifications } from "../../../../context/NotificationContext";
import { CalendarEventType } from "../../../../types/calendar";
import { GetTransactionsResponse } from "../../../../types/transactions";
import { takeNewSyncEvents } from "../../../../utils/syncEventCursor";
import { FetchAppointmentsAndTimeBlocks } from "./useCalendarData";

interface UseRealtimeSyncArgs {
  transactions: GetTransactionsResponse[];
  events: CalendarEventType[];
  refetchAndUpdateTransaction: (
    transactionId: string,
    errorContext: string,
  ) => Promise<void>;
  fetchAppointmentsAndTimeBlocks: FetchAppointmentsAndTimeBlocks;
}

/**
 * Pusher-driven calendar refresh: appointment.created/updated notifications
 * and cross-client transaction.sync events each refetch the affected
 * transaction (time_off syncs refetch the viewed range).
 */
export function useRealtimeSync({
  transactions,
  events,
  refetchAndUpdateTransaction,
  fetchAppointmentsAndTimeBlocks,
}: UseRealtimeSyncArgs) {
  const { notifications: pusherNotifications, pusherSyncEvents } =
    useNotifications();

  // Track processed notification IDs to avoid duplicate fetches
  const [processedNotificationIds, setProcessedNotificationIds] = useState<
    Set<string>
  >(new Set());

  // Last transaction.sync seq processed. CalendarView is the always-mounted
  // consumer, so it starts at 0 and processes every event of the session
  // (on-demand consumers like useAppointmentForms lazy-init to the latest seq).
  const lastSeenSyncSeqRef = useRef(0);

  // Handle new appointment notifications from Pusher - fetch single transaction and back-populate
  useEffect(() => {
    const handleNewAppointmentNotifications = async () => {
      if (!pusherNotifications || pusherNotifications.length === 0) return;

      // Filter for unread appointment notifications that haven't been processed yet
      const newAppointmentNotifications = pusherNotifications.filter(
        (notification) =>
          !notification.is_read &&
          (notification.event_name === "appointment.created" ||
            notification.event_name === "appointment.updated") &&
          notification.body?.target_id &&
          !processedNotificationIds.has(notification.notification_id),
      );

      // Process each new notification
      for (const notification of newAppointmentNotifications) {
        const transactionId = notification.body.target_id;

        // Ensure transactionId exists and is a string
        if (!transactionId) continue;

        // Mark this notification as processed immediately to avoid duplicate fetches
        setProcessedNotificationIds((prev) => {
          const newSet = new Set(prev);
          newSet.add(notification.notification_id);
          return newSet;
        });

        // Check if we already have this transaction
        const existingTransaction = transactions.find(
          (t) => t.transaction.id === transactionId,
        );
        const existingEvent = events.find(
          (event) =>
            "transactionId" in event && event.transactionId === transactionId,
        );

        // Determine if we should fetch:
        // - For appointment.created: only fetch if transaction doesn't exist
        // - For appointment.updated: always fetch to get updated data
        const isUpdateEvent = notification.event_name === "appointment.updated";
        const shouldFetch =
          isUpdateEvent || !existingTransaction || !existingEvent;

        if (shouldFetch) {
          await refetchAndUpdateTransaction(transactionId, "notification");
        }
      }
    };

    handleNewAppointmentNotifications();
  }, [
    pusherNotifications,
    processedNotificationIds,
    transactions,
    events,
    refetchAndUpdateTransaction,
  ]);

  // Handle cross-client sync events from core/payments mutations.
  // Sync events do NOT live in the notifications list — they sit in a separate
  // bounded buffer. We track the last seq processed so each event triggers
  // exactly one refetch of the affected data, even past the buffer cap.
  useEffect(() => {
    const { newEvents, lastSeenSeq } = takeNewSyncEvents(
      pusherSyncEvents,
      lastSeenSyncSeqRef.current,
    );
    if (newEvents.length === 0) return;
    lastSeenSyncSeqRef.current = lastSeenSeq;

    const transactionIdsToRefetch = new Set<string>();
    let needsTimeBlockRefetch = false;
    for (const ev of newEvents) {
      if (ev?.entity === "time_off") {
        // time_off create/update/delete has no parent transaction; a single
        // refetch of the currently-viewed range covers every time_off event
        // in the batch, and the merge-with-eviction path in
        // fetchAppointmentsAndTimeBlocks removes rows deleted on another
        // device.
        needsTimeBlockRefetch = true;
        continue;
      }
      if (ev?.transaction_id) transactionIdsToRefetch.add(ev.transaction_id);
    }

    const handleSyncRefetches = async () => {
      const ids = Array.from(transactionIdsToRefetch);
      for (const transactionId of ids) {
        await refetchAndUpdateTransaction(transactionId, "sync event");
      }
      if (needsTimeBlockRefetch) {
        await fetchAppointmentsAndTimeBlocks(undefined, undefined, undefined, {
          replaceAll: false,
        });
      }
    };

    handleSyncRefetches();
  }, [
    pusherSyncEvents,
    refetchAndUpdateTransaction,
    fetchAppointmentsAndTimeBlocks,
  ]);
}
